import { useTranslate } from "helpers";
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import { ContactPhoneCardProps, ContactPhoneRef } from "./types";
import { NoResults } from "ui/components/NoResults";
import ContactPhoneSearchResults from "./components/ContactPhoneSearchResults/ContactPhoneSearchResults";
import ContactPhoneEditCard from "../ContactPhoneEditCard/ContactPhoneEditCard";
import { deleteContactPhoneAPI, getContactPhonesAPI } from "services/api/contacts/api";
import { ContactPhoneRow } from "services/api/contacts/types";
import { Dialog } from "ui/components/Dialog";



const ContactPhoneCard = forwardRef<ContactPhoneRef, ContactPhoneCardProps>((props, ref) => {
  const { contactDetail } = props;

  const { t } = useTranslate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<ContactPhoneRow>
  >(initialDataWithPagination);

  const [phoneId, setPhoneId] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);


  useEffect(() => {
    if (contactDetail.id)
      fetchSearchResult(searchResults.pagination, contactDetail.id);
  }, [contactDetail.id]);

  const fetchSearchResult = async (pagination: Pagination, contactId: string) => {
    setIsLoading(true);
    try {
      const results = await getContactPhonesAPI(pagination, contactId);

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    if (id) {
      setPhoneId(id);
      setShowAddNewModal(true);
    }
  }, []);

  const handlePhoneClick = useCallback((event: React.MouseEvent<HTMLSpanElement>, phone: string) => {
    event.stopPropagation();
    event.preventDefault();
    window.location.href = `tel:${phone}`;
  }, []);

  const handleWhatsAppClick = useCallback((event: React.MouseEvent<HTMLSpanElement>, phone: string) => {
    event.stopPropagation();
    event.preventDefault();
    const cleanPhone = phone.replace(/\D/g, '');
    // WhatsApp uses international format without +
    const internationalPhone = cleanPhone.startsWith('0')
      ? '385' + cleanPhone.substring(1)
      : cleanPhone;
    window.open(`https://wa.me/${internationalPhone}`, '_blank', 'noopener,noreferrer');
  }, []);

  const handleViberClick = useCallback((event: React.MouseEvent<HTMLSpanElement>, phone: string) => {
    event.stopPropagation();
    event.preventDefault();
    const cleanPhone = phone.replace(/\D/g, '');
    // Viber uses international format with +
    const internationalPhone = cleanPhone.startsWith('0')
      ? '+385' + cleanPhone.substring(1)
      : '+' + cleanPhone;
    window.open(`viber://chat?number=${encodeURIComponent(internationalPhone)}`, '_blank');
  }, []);

  const openAddNew = () => {
    setPhoneId("");
    setShowAddNewModal(true);
  };

  const handleEditSuccess = useCallback(() => {
    if (contactDetail.id)
      fetchSearchResult(searchResults.pagination, contactDetail.id);
    setShowAddNewModal(false)
  }, [contactDetail.id, searchResults.pagination]);

  const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
    event.stopPropagation();
    event.preventDefault();

    setPhoneId(id);
    setOpenDialog(true);
  }

  const handleDeleteDialogConfirm = useCallback(async () => {
    if (!phoneId) return;

    try {
      if (contactDetail.id) {
        const deleted = await deleteContactPhoneAPI(
          t,
          contactDetail.id,
          phoneId,
        );

        if (deleted) {
          await fetchSearchResult(searchResults.pagination, contactDetail.id);
        }
      }

      // zatvori dijalog nakon brisanja
      setOpenDialog(false);
      setPhoneId("");
    } catch (err) {
      console.error("Greška prilikom brisanja phone-a:", err);
    }
  }, [phoneId, t, contactDetail.id, searchResults.pagination]);

  const handleDeleteDialogClose = useCallback(() => {
    setPhoneId("");
    setOpenDialog(false);
  }, []);

  useImperativeHandle(ref, () => ({
    openAddNew,
  }));

  if (!contactDetail.id) return <></>;


  return (
    <>
      <div className="mx-[-1px]">
        {!isLoading && searchResults.pagination.totalPages === 0 ? (
          <NoResults  message={t("admin.contact.phone.search.noresults")} />
        ) : (
          <ContactPhoneSearchResults
            searchResults={searchResults}
            selectedRows={[]}
            isLoading={isLoading}
            onRowClick={handleRowClick}
            onPhoneClick={handlePhoneClick}
            onDeleteClick={handleDeleteClick}
            onWhatsAppClick={handleWhatsAppClick}
            onViberClick={handleViberClick}
          />
        )}
      </div>

      <ContactPhoneEditCard
        phoneId={phoneId}
        contactId={contactDetail.id}
        show={showAddNewModal}
        onSuccess={handleEditSuccess}
        onClose={() => setShowAddNewModal(false)}
      />
      <Dialog
        open={openDialog}
        title={t(
          "admin.contact.phone.dialog.delete.title"
        )}
        message={t(
          "admin.contact.phone.dialog.delete.message"
        )}
        confirmText={t(
          "admin.contact.phone.dialog.delete.confirmtext"
        )}
        cancelText={t(
          "admin.contact.phone.dialog.delete.canceltext"
        )}
        onConfirm={handleDeleteDialogConfirm}
        onCancel={handleDeleteDialogClose}
      />
    </>
  );
});

export default memo(ContactPhoneCard);

